import {
  Button,
  Toolbar,
  ToolbarItem,
  ToolbarSeparator,
} from "@progress/kendo-react-buttons";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { cloneElement, Fragment, useRef, useState } from "react";
import { process } from "@progress/kendo-data-query";
import { useSelector } from "react-redux";
import { SvgIcon } from "@progress/kendo-react-common";
import { NavLink } from "react-router-dom";
import { formatDate } from "../../../lib/utils";
import {
  bellIcon,
  downloadIcon,
  checkCircleIcon,
  exclamationCircleIcon,
  fileExcelIcon,
} from "@progress/kendo-svg-icons";

import i18next from "i18next";
import { useTranslation } from "react-i18next";

export const BillingInvoice = (props) => {
  const currentTheme = useSelector((state) => state.currentTheme);
  const currentUser = useSelector((state) => state.currentUser);
  const pageState = useSelector((state) => state.accountManagementPage);
  const [page, setPage] = useState({ skip: 0, take: 10 });
  const [selectedId, setSelectedId] = useState(null);
  const excelExportRef = useRef(null);

  const { t } = useTranslation();
  const exportToExcel = () => {
    if (excelExportRef.current) {
      excelExportRef.current.save();
    }
  };

  const toolbarRenderer = () => (
    <Toolbar
      style={{ borderColor: "transparent", backgroundColor: "transparent" }}
    >
      <Fragment>
        {/* <ToolbarItem>
          <Button
            style={{
              color: currentTheme.primary,
              border: 0,
              paddingLeft: 0,
              backgroundColor: "transparent",
            }}
            icon="cancel"
            onClick={props.onCancelSubsciption}
          >
            {t("accountmang.tabs.cancelsub")}
          </Button>
        </ToolbarItem>
        <ToolbarSeparator /> */}
        <ToolbarItem>
          <Button
            style={{
              color: currentTheme.primary,
              border: 0,
              paddingLeft: 0,
              backgroundColor: "transparent",
            }}
            icon="file-excel"
            onClick={exportToExcel}
          >
             {t("accountmang.tabs.exportexcel")}
          </Button>
        </ToolbarItem>
      </Fragment>
    </Toolbar>
  );

  const statusCellRenderer = (props) => {
    const { dataItem } = props;
    const statusColor = dataItem.Status === "paid" ? "green" : "red";

    return (
      <td>
        <SvgIcon
          color={statusColor}
          icon={
            dataItem.Status === "paid" ? checkCircleIcon : exclamationCircleIcon
          }
          size="small"
          style={{ marginRight: "3px" }}
        />
        {dataItem.Status}
      </td>
    );
  };

  const invoiceUrlCellRenderer = (props) => (
    <td>
      <a href={props.dataItem.InvoiceUrl} target="_blank">
        <SvgIcon icon={downloadIcon} size="small" /> {t("accountmang.tabs.download")}
      </a>
    </td>
  );

  const invDateCellRenderer = (props) => (
    <td>{formatDate(props.dataItem.PaymentDate, "dd-mm-yyyy")}</td>
  );

  const subPeriodDateCellRenderer = (props) => (
    <td>
      {formatDate(props.dataItem.SubStartDate, "dd-mm-yyyy")} -{" "}
      {formatDate(props.dataItem.SubEndDate, "dd-mm-yyyy")}
    </td>
  );

  const handleRowClick = (e) => {
    setSelectedId(e.dataItem.id);
  };

  return currentUser.IsServiceSubscriptionRootUser ? (
    <div>
      <h4>{t("accountmang.tabs.bill")}</h4>
      {toolbarRenderer()}

      {currentUser.Subscription?.IsStopped && (
        <div className="error-highlighted">
          <div>
            <SvgIcon icon={bellIcon} size="small" /> {t("accountmang.tabs.payreq")}
          </div>
          <NavLink to="/accountmanagement" className="btnPay">
            {t("accountmang.tabs.paynow")}
          </NavLink>
        </div>
      )}

      <div className="responsive-grid-container">
        {pageState.formValues.Billings.length === 0 ? (
          <div>
            <p>{t("accountmang.tabs.invoice1")}</p>
            <Button onClick={props.onAddInvoice}   style={{
                padding: 8,
                color: currentTheme.touchColor,
                fontWeight: 500,
              }}>{t("accountmang.tabs.invoice2")}</Button>
          </div>
        ) : (
          <ExcelExport
            data={pageState.formValues.Billings}
            ref={excelExportRef}
          >
            <Grid
              data={process(pageState.formValues.Billings, {
                skip: page.skip,
                take: page.take,
              })}
              style={{ height: "400px" }}
              pageable
              total={pageState.formValues.Billings.length}
              skip={page.skip}
              take={page.take}
              onPageChange={(e) => setPage(e.page)}
              rowRender={(row, rowProps) =>
                cloneElement(row, {
                  style: {
                    backgroundColor:
                      rowProps.dataItem.id === selectedId ? "#f0f8ff" : "",
                  },
                })
              }
              onRowClick={handleRowClick}
            >
              <GridColumn field="InvoiceNumber" title={t("accountmang.tabs.invoiceid")} />
              <GridColumn
                field="PaymentDate"
                title={t("accountmang.tabs.invoicedate")}
                cell={invDateCellRenderer}
              />
              <GridColumn
                field="SubStartDate"
                title={t("accountmang.tabs.billing")}
                cell={subPeriodDateCellRenderer}
              />
              <GridColumn field="AmountRemaining" title={t("accountmang.tabs.due")} />
              <GridColumn field="AmountPaid" title={t("accountmang.tabs.paid")} />
              <GridColumn
                field="InvoiceUrl"
                title={t("accountmang.tabs.download")} 
                cell={invoiceUrlCellRenderer}
              />
              <GridColumn
                field="Status"
                title={t("accountmang.tabs.status")}
                cell={statusCellRenderer}
              />
            </Grid>
          </ExcelExport>
        )}
      </div>

      <style>
        {`
          .responsive-grid-container {
            width: 100%;
            overflow-x: auto;
          }
          .error-highlighted {
            color: red;
            margin-bottom: 10px;
            display: flex;
            align-items: center;
            gap: 5px;
            justify-content: space-between;
          }
          .responsive-grid-container .k-grid-pager {
            display: flex;
          }
          .accountmanagement-page .btnPay {
            color: #fff;
            background-color: ${currentTheme.touchColor};
            border: 1px solid #fff;
            border-radius: 3px;
            padding: 2px 10px 3px;
            cursor: pointer;
            margin-right: 20px;
            float: right;
          }
        `}
      </style>
    </div>
  ) : null;
};
